/**
 * Local-first ASSESSMENT mutations (F3-B).
 *
 * Write to PowerSync → write-path (`db/sync.rs`), which applies authz:
 *   • assignment (content): author/editor of the course.
 *   • assignment_submission: ACTIVE enrolled student on their own row
 *     (user_id forced to the actor; `late` sealed by the server).
 *   • assignment_grade / grade_category / grade_item: cohort manager
 *     (org/campus_admin or lead teacher).
 *
 * Grades can also be applied in bulk over REST (`/cohorts/:id/grades/bulk`)
 * and the final grade is computed server-authoritative when the cohort closes.
 */

import {
  and,
  assignment,
  assignment_grade,
  assignment_submission,
  eq,
  grade_category,
  grade_item,
  type PowerSyncDrizzleDB,
} from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateId } from "@fleet/utils";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  withScope,
} from "../../powersync/verified-write";
import type { AssignmentKind, Rubric, SubmissionType } from "../modules/assignment-plugin";
import { addCourseModuleMutation } from "../modules/mutations";
import { defaultItemMaxScore, type GradingDisplayStrategy } from "../grading/registry";

/**
 * Course/cohort's ACTIVE scale, already resolved by the caller
 * (`resolveActiveGradingModel`). MANDATORY when creating an evaluable item:
 * the max is DERIVED from it instead of falling back to a constant.
 *
 * There used to be a `?? 100` here and in eight other places. It wasn't
 * "100 instead of 20": it was a constant where configuration should have
 * been, and it contradicted the active scale whatever it was. Requiring the
 * scale in the signature keeps the call site from deciding it on its own.
 */
export type ActiveGradingScale = Pick<GradingDisplayStrategy, "maxValue">;

// ─── Assignments (course content) ─────────────────────────────────────────────

export interface CreateAssignmentInput {
  courseId: string;
  organizationId?: string | null;
  /** Course's active scale — the max comes from it unless one is given explicitly. */
  scale: ActiveGradingScale;
  title?: string;
  instructions?: string | null;
  kind?: AssignmentKind;
  maxScore?: number;
  rubric?: Rubric | null;
  submissionType?: SubmissionType;
  groupMode?: boolean;
}

export async function createAssignmentMutation(
  db: PowerSyncDrizzleDB,
  input: CreateAssignmentInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(assignment).values({
    id,
    course_id: input.courseId,
    organization_id: input.organizationId ?? null,
    title: input.title?.trim() || "Nueva tarea",
    instructions: input.instructions ?? null,
    kind: input.kind ?? "homework",
    max_score: input.maxScore ?? defaultItemMaxScore(input.scale),
    rubric: input.rubric ? JSON.stringify(input.rubric) : null,
    submission_type: input.submissionType ?? "text",
    group_mode: input.groupMode ? 1 : 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * ⚠️ No `scope` on purpose, even though `assignment` DOES have `organization_id`.
 * 2026-08-13 audit: `assignment` (content) is authorized as "author/editor
 * OF THE COURSE" (see file header) — the same USER boundary as
 * `resource`/`forum` (`academy/modules/mutations.ts`,
 * `academy/community/mutations.ts`), not organization. And it's created from
 * the same authoring flow (`addAssignmentModuleMutation`,
 * `course-editor.tsx` → without `organizationId`), so the column stays NULL
 * on that path — a scope by active organization wouldn't protect anything
 * and would break every edit. Different from `grade_category`/`grade_item`
 * (below), which ARE "cohort manager: ManageAcademics" — a real
 * organization capability.
 */
export async function updateAssignmentMutation(
  db: PowerSyncDrizzleDB,
  assignmentId: string,
  updates: Partial<{
    title: string;
    instructions: string | null;
    kind: AssignmentKind;
    max_score: number;
    rubric: Rubric | null;
    submission_type: SubmissionType;
    group_mode: boolean;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  // `updated_at` gets stamped by `patchRowVerified`; not included here (if it
  // came in the patch, the helper's post-write comparison would never
  // match — it'd always be a different `nowISO()` — and the retry would
  // blow up for a false reason, the bug measured in `agape-church-app`).
  const patch: Record<string, unknown> = {};
  if (updates.title !== undefined) {
    patch.title = updates.title;
  }
  if (updates.instructions !== undefined) {
    patch.instructions = updates.instructions;
  }
  if (updates.kind !== undefined) {
    patch.kind = updates.kind;
  }
  if (updates.max_score !== undefined) {
    patch.max_score = updates.max_score;
  }
  if (updates.rubric !== undefined) {
    patch.rubric = updates.rubric ? JSON.stringify(updates.rubric) : null;
  }
  if (updates.submission_type !== undefined) {
    patch.submission_type = updates.submission_type;
  }
  if (updates.group_mode !== undefined) {
    patch.group_mode = updates.group_mode ? 1 : 0;
  }
  await patchRowVerified(db, assignment, assignment.id, assignmentId, patch, options);
}

/** ⚠️ No `scope` on purpose — see the note on `updateAssignmentMutation`. */
export async function deleteAssignmentMutation(
  db: PowerSyncDrizzleDB,
  assignmentId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    assignment,
    assignment.id,
    assignmentId,
    { deleted_at: nowISO() },
    options,
  );
}

/**
 * COMPOSITE (authoring): creates the assignment AND its `course_module` row
 * (kind='assignment', ref_id=assignment.id) — just like addResourceModuleMutation.
 */
export async function addAssignmentModuleMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    unitId: string;
    organizationId?: string | null;
    /** Course's active scale (see `ActiveGradingScale`). */
    scale: ActiveGradingScale;
    title?: string;
    position?: number;
  },
): Promise<{ assignmentId: string; moduleId: string }> {
  const assignmentId = await createAssignmentMutation(db, {
    courseId: params.courseId,
    organizationId: params.organizationId,
    scale: params.scale,
    title: params.title,
  });
  const moduleId = await addCourseModuleMutation(db, {
    courseId: params.courseId,
    unitId: params.unitId,
    kind: "assignment",
    refId: assignmentId,
    position: params.position ?? 0,
  });
  return { assignmentId, moduleId };
}

// ─── Student's submission ──────────────────────────────────────────────────────

/**
 * The STUDENT submits (or updates) their assignment local-first. `late` is
 * sealed by the server; here only body/files. Manual upsert by
 * (assignment,cohort,user) (PowerSync doesn't support UPSERT over views).
 *
 * ⚠️ Class 2 (POWERSYNC_VERIFIED_WRITE.md §3): the SELECT that decides
 * INSERT-vs-UPDATE lives INSIDE the window. If the window empties it out, it
 * doesn't lose the data: it generates a NEW row with a random id → two
 * active submissions for the same (assignment, cohort, user).
 * `patchRowVerified` can't shield it because there's no known `id` until
 * AFTER deciding the branch, and there's no unique index in the schema for
 * an `ON CONFLICT DO UPDATE`. Without a caller bringing a cached id from an
 * earlier submission in this session (the way `saveLessonProgressMutation`
 * does with `progressId`), the only honest coverage today is documenting it — characterized in
 * `__tests__/mutations.test.ts` ("the window in submitAssignmentMutation").
 * Find it with `git grep -n -B8 '\.insert(' … mutations.ts` (§3 of the guide).
 */
export async function submitAssignmentMutation(
  db: PowerSyncDrizzleDB,
  params: {
    assignmentId: string;
    cohortId: string;
    organizationId: string;
    userId: string;
    body?: string | null;
    files?: string[];
  },
): Promise<string> {
  const now = nowISO();
  const rows = await db
    .select({ id: assignment_submission.id })
    .from(assignment_submission)
    .where(
      and(
        eq(assignment_submission.assignment_id, params.assignmentId),
        eq(assignment_submission.cohort_id, params.cohortId),
        eq(assignment_submission.user_id, params.userId),
      ),
    );
  const values = {
    body: params.body ?? null,
    files: JSON.stringify(params.files ?? []),
    submitted_at: now,
    updated_at: now,
  };
  if (rows[0]) {
    await db
      .update(assignment_submission)
      .set(values)
      .where(eq(assignment_submission.id, rows[0].id));
    return rows[0].id;
  }
  const id = generateId();
  await db.insert(assignment_submission).values({
    id,
    assignment_id: params.assignmentId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    user_id: params.userId,
    late: 0,
    created_at: now,
    ...values,
  });
  return id;
}

// ─── Grading (manager; local-first) ────────────────────────────────────────────

/**
 * ⚠️ Class 2, same shape as `submitAssignmentMutation` (see its comment): the
 * SELECT by (assignment, cohort, user) that decides INSERT-vs-UPDATE can
 * come back empty inside the window and duplicate the grade row. Without a
 * cached id from the caller, it's documented and characterized with a test
 * instead of faking coverage.
 */
export async function upsertAssignmentGradeMutation(
  db: PowerSyncDrizzleDB,
  params: {
    assignmentId: string;
    cohortId: string;
    organizationId: string;
    userId: string;
    submissionId?: string | null;
    score?: number | null;
    rubricScores?: Record<string, number> | null;
    feedback?: string | null;
    status?: "pending" | "graded" | "excused" | "returned";
  },
): Promise<string> {
  const now = nowISO();
  const rows = await db
    .select({ id: assignment_grade.id })
    .from(assignment_grade)
    .where(
      and(
        eq(assignment_grade.assignment_id, params.assignmentId),
        eq(assignment_grade.cohort_id, params.cohortId),
        eq(assignment_grade.user_id, params.userId),
      ),
    );
  const values = {
    submission_id: params.submissionId ?? null,
    score: params.score ?? null,
    rubric_scores: params.rubricScores ? JSON.stringify(params.rubricScores) : null,
    feedback: params.feedback ?? null,
    status: params.status ?? "graded",
    graded_at: now,
    updated_at: now,
  };
  if (rows[0]) {
    await db.update(assignment_grade).set(values).where(eq(assignment_grade.id, rows[0].id));
    return rows[0].id;
  }
  const id = generateId();
  await db.insert(assignment_grade).values({
    id,
    assignment_id: params.assignmentId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    user_id: params.userId,
    created_at: now,
    ...values,
  });
  return id;
}

// ─── Gradebook: categories and items (manager) ─────────────────────────────────

export async function createGradeCategoryMutation(
  db: PowerSyncDrizzleDB,
  params: {
    cohortId: string;
    organizationId: string;
    name?: string;
    weight?: number;
    dropLowest?: number;
    position?: number;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(grade_category).values({
    id,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    name: params.name?.trim() || "Nueva categoría",
    weight: params.weight ?? 1,
    drop_lowest: params.dropLowest ?? 0,
    position: params.position ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * `organizationId`: the org the caller ALREADY has in context (`grades-tab`,
 * inside `CohortDetailView` — `props.organizationId`). The gradebook is
 * "cohort manager: ManageAcademics" (see file header) — a real
 * organization capability, unlike `assignment` (see the note on
 * `updateAssignmentMutation`).
 */
export async function updateGradeCategoryMutation(
  db: PowerSyncDrizzleDB,
  categoryId: string,
  updates: Partial<{ name: string; weight: number; drop_lowest: number; position: number }>,
  organizationId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    grade_category,
    grade_category.id,
    categoryId,
    updates,
    withScope(options, [{ column: grade_category.organization_id, value: organizationId }]),
  );
}

/** `organizationId`: see the note on `updateGradeCategoryMutation`. */
export async function deleteGradeCategoryMutation(
  db: PowerSyncDrizzleDB,
  categoryId: string,
  organizationId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    grade_category,
    grade_category.id,
    categoryId,
    { deleted_at: nowISO() },
    withScope(options, [{ column: grade_category.organization_id, value: organizationId }]),
  );
}

export async function createGradeItemMutation(
  db: PowerSyncDrizzleDB,
  params: {
    cohortId: string;
    organizationId: string;
    categoryId?: string | null;
    sourceKind?: "assignment" | "auto_blocks" | "attendance" | "manual";
    sourceRef?: string | null;
    name?: string;
    /** Cohort's active scale (see `ActiveGradingScale`). */
    scale: ActiveGradingScale;
    maxScore?: number;
    weight?: number;
    position?: number;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(grade_item).values({
    id,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    category_id: params.categoryId ?? null,
    source_kind: params.sourceKind ?? "manual",
    source_ref: params.sourceRef ?? null,
    name: params.name?.trim() || "Nuevo ítem",
    max_score: params.maxScore ?? defaultItemMaxScore(params.scale),
    weight: params.weight ?? 1,
    position: params.position ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * ⚠️ Dead code (POWERSYNC_VERIFIED_WRITE.md §8): no callers in `apps/web`
 * (only `createGradeItemMutation` has UI — there is no editor or delete for
 * an already-created `grade_item`). Not shielded with `patchRowVerified`
 * blindly (lesson from `malla-vpn-app`, where 33 mutations were protected
 * with no route consuming them): if an editor is ever wired up, convert it then.
 */
export async function updateGradeItemMutation(
  db: PowerSyncDrizzleDB,
  itemId: string,
  updates: Partial<{
    category_id: string | null;
    source_kind: string;
    source_ref: string | null;
    name: string;
    max_score: number;
    weight: number;
    position: number;
  }>,
): Promise<void> {
  await db
    .update(grade_item)
    .set({ ...updates, updated_at: nowISO() })
    .where(eq(grade_item.id, itemId));
}

/** ⚠️ Dead code — see `updateGradeItemMutation`. */
export async function deleteGradeItemMutation(
  db: PowerSyncDrizzleDB,
  itemId: string,
): Promise<void> {
  await db
    .update(grade_item)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(grade_item.id, itemId));
}
